import React, { useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { SignIn, SignUp, useAuth as useClerkAuth } from '@clerk/react';
import { Webhook, Database, ShieldCheck } from 'lucide-react';
import { Spinner } from '../components/ui';

const PERKS = [
  { icon: Webhook, label: 'Real-time webhook lead capture' },
  { icon: Database, label: 'Automated multi-platform backups' },
  { icon: ShieldCheck, label: 'Bank-grade encrypted vault' },
];

/** Split-screen auth layout: brand panel left, Clerk component (or any content) right. */
export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      {/* Brand panel */}
      <div
        className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 text-white"
        style={{ background: 'linear-gradient(150deg,var(--accent-hover) 0%,var(--accent) 42%,var(--cyan) 118%)' }}
      >
        <div className="absolute -top-32 -right-24 w-[380px] h-[380px] rounded-full bg-white/10 blur-[10px]" />
        <div className="absolute -bottom-36 -left-16 w-[320px] h-[320px] rounded-full bg-white/[0.07]" />

        <div className="relative flex items-center gap-3">
          <div className="w-[38px] h-[38px] rounded-[10px] bg-white flex items-center justify-center shadow-sm">
            <img src="/icon.png" alt="" className="w-6 h-6" />
          </div>
          <span className="font-display font-bold text-[17px] tracking-tight">My Lead Syncer</span>
        </div>

        <div className="relative max-w-[380px]">
          <h2 className="m-0 font-display font-bold text-[34px] leading-[1.18] tracking-tight">
            Every lead, captured and backed up — automatically.
          </h2>
          <div className="mt-7 flex flex-col gap-4">
            {PERKS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <span className="w-[30px] h-[30px] rounded-[9px] bg-white/[0.16] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4" />
                </span>
                <span className="font-semibold text-[14px] opacity-95">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative p-5 rounded-[14px] bg-white/[0.12] backdrop-blur max-w-[400px]">
          <p className="m-0 font-medium text-[14px] leading-relaxed opacity-95">
            “Set up once and forget it — we haven't lost a single lead since switching to My Lead Syncer.”
          </p>
          <div className="mt-3 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-[12px]">CV</div>
            <div className="leading-tight">
              <div className="font-bold text-[12.5px]">Carlos Vargas</div>
              <div className="font-medium text-[11.5px] opacity-80">Bezalel Digital</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-8 min-h-screen lg:min-h-0">
        <div className="w-full max-w-[420px] flex justify-center animate-fade">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * /login/* → Clerk <SignIn/>, /register/* → Clerk <SignUp/> (path routing, so
 * Clerk's own sub-steps — factor-two, verify email, reset password — live under
 * these paths). After sign-in Clerk goes to /dashboard; after sign-up to
 * /billing, where the free-trial claim lives (see ClerkBoot redirect props).
 */
export default function Login({ mode = 'sign-in' }: { mode?: 'sign-in' | 'sign-up' }) {
  const { isLoaded, isSignedIn } = useClerkAuth();
  // Only bounce users who arrive already signed in; a sign-in/up completing on
  // this page is redirected by Clerk itself (to /dashboard or /billing).
  const signedInOnArrival = useRef<boolean | null>(null);
  if (isLoaded && signedInOnArrival.current === null) signedInOnArrival.current = !!isSignedIn;

  if (!isLoaded) {
    return (
      <AuthShell>
        <Spinner />
      </AuthShell>
    );
  }

  if (signedInOnArrival.current) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthShell>
      {mode === 'sign-up' ? (
        <SignUp routing="path" path="/register" signInUrl="/login" />
      ) : (
        <SignIn routing="path" path="/login" signUpUrl="/register" />
      )}
    </AuthShell>
  );
}
